import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Button,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'February 17, 2025';

function QuranPrivacyPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, color: 'primary.main' }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#2e7d32' }}>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Quran App / Deen Companion — Last updated: {LAST_UPDATED}
          </Typography>

          <Typography variant="body1" paragraph>
            Baiglabs (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the Quran App (Deen Companion) mobile application.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our app.
            Please read this policy carefully. By using the Quran App, you consent to the practices described herein.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We may collect information in the following ways:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <strong>Device Information:</strong> Device type, operating system, unique device identifiers,
              and app version for compatibility and crash reporting.
            </li>
            <li>
              <strong>Location Data:</strong> If you enable location services, we use your approximate location
              to provide accurate prayer times for your area. This data is processed locally when possible and
              is not shared with third parties for advertising.
            </li>
            <li>
              <strong>App Preferences:</strong> Your settings such as prayer time calculation method, notification
              preferences, and reading/bookmark preferences stored on your device.
            </li>
            <li>
              <strong>Usage Data:</strong> Anonymous analytics (e.g., feature usage, crash reports) to improve the app.
            </li>
          </Box>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use collected information to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Provide and maintain prayer times, Quran content, and dhikr features</li>
            <li>Personalize your experience (e.g., location-based prayer times)</li>
            <li>Send notifications you have opted into (e.g., prayer reminders)</li>
            <li>Improve app performance and fix bugs</li>
            <li>Comply with legal obligations</li>
          </Box>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell your personal information. We may share data only in these limited circumstances:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our app
              (e.g., analytics, crash reporting), under strict confidentiality agreements.</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, safety, or property.</li>
            <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
          </Box>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4. Data Storage and Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to protect your data. Much of the app&apos;s
            data is stored locally on your device. Any data transmitted to our servers is encrypted in transit.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            5. Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your jurisdiction, you may have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Access, correct, or delete your personal data</li>
            <li>Opt out of certain data collection (e.g., via device settings)</li>
            <li>Withdraw consent where applicable</li>
            <li>Lodge a complaint with a supervisory authority (e.g., under GDPR)</li>
          </Box>
          <Typography variant="body1" paragraph>
            To exercise these rights, contact us at the email below.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6. Children&apos;s Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our app does not knowingly collect personal information from children under 13. If you are a parent or
            guardian and believe your child has provided us with personal information, please contact us and we
            will delete it promptly.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7. International Users
          </Typography>
          <Typography variant="body1" paragraph>
            Your data may be processed in countries where our servers or service providers operate. By using the app,
            you consent to such transfer. We comply with applicable data protection laws including GDPR for users
            in the European Economic Area.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            8. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting
            the updated policy on this page and updating the &quot;Last updated&quot; date. Your continued use of the app
            after changes constitutes acceptance of the revised policy.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            9. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            For privacy-related questions or requests, contact us at:
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Link href="https://baiglabs.com/contact" target="_blank" rel="noopener noreferrer" color="primary">
              https://baiglabs.com/contact
            </Link>
          </Box>
          <Typography variant="body1">
            Baiglabs — Quran App / Deen Companion
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default QuranPrivacyPage;
